/**
 * Status badge for Telnyx Brand / Campaign / Number states.
 *
 * Renders a tinted pill with a hairline ring and a contextual icon. PENDING
 * states get a soft pulse so the operator can see at a glance that something
 * is in flight (vetting, carrier approval, etc.).
 */
import { cn } from '@respectly/ui';
import {
  CircleDashed,
  CheckCircle2,
  XCircle,
  Clock4,
  Sparkles,
  ShieldCheck,
} from 'lucide-react';

type RawStatus = string | null | undefined;

interface TelnyxStatusBadgeProps {
  status: RawStatus;
  size?: 'sm' | 'md';
  /** When true, render a "Not started" version instead of nothing for null. */
  unsetLabel?: string;
}

interface StatusVisual {
  label: string;
  icon: typeof CircleDashed;
  classes: string;
  pulse: boolean;
}

function classify(status: RawStatus, unsetLabel: string): StatusVisual {
  const norm = (status ?? '').toUpperCase();

  switch (norm) {
    case 'APPROVED':
    case 'VERIFIED':
    case 'VETTED_VERIFIED':
      return {
        label: norm === 'VETTED_VERIFIED' ? 'Vetted' : 'Approved',
        icon: norm === 'VETTED_VERIFIED' ? ShieldCheck : CheckCircle2,
        classes:
          'bg-emerald-50 text-emerald-700 ring-emerald-200/70 dark:bg-emerald-950/40 dark:text-emerald-300 dark:ring-emerald-800/50',
        pulse: false,
      };
    case 'REJECTED':
    case 'EXPIRED':
      return {
        label: norm === 'EXPIRED' ? 'Expired' : 'Rejected',
        icon: XCircle,
        classes:
          'bg-rose-50 text-rose-700 ring-rose-200/70 dark:bg-rose-950/40 dark:text-rose-300 dark:ring-rose-800/50',
        pulse: false,
      };
    case 'PENDING':
    case 'UNVERIFIED':
      return {
        label: norm === 'UNVERIFIED' ? 'Unverified' : 'Pending vetting',
        icon: Clock4,
        classes:
          'bg-amber-50 text-amber-700 ring-amber-200/70 dark:bg-amber-950/40 dark:text-amber-300 dark:ring-amber-800/50',
        pulse: true,
      };
    case '':
      return {
        label: unsetLabel,
        icon: CircleDashed,
        classes:
          'bg-slate-50 text-slate-500 ring-slate-200 dark:bg-slate-900/40 dark:text-slate-400 dark:ring-slate-800',
        pulse: false,
      };
    default:
      return {
        label: norm.replace(/_/g, ' ').toLowerCase(),
        icon: Sparkles,
        classes:
          'bg-sky-50 text-sky-700 ring-sky-200/70 dark:bg-sky-950/40 dark:text-sky-300 dark:ring-sky-800/50',
        pulse: false,
      };
  }
}

export function TelnyxStatusBadge({
  status,
  size = 'md',
  unsetLabel = 'Not started',
}: TelnyxStatusBadgeProps) {
  const visual = classify(status, unsetLabel);
  const Icon = visual.icon;

  const sizing =
    size === 'sm'
      ? 'h-5 px-1.5 text-[10px] gap-1'
      : 'h-6 px-2 text-[11px] gap-1.5';

  return (
    <span
      className={cn(
        'inline-flex items-center rounded-full font-semibold uppercase tracking-[0.05em] ring-1',
        sizing,
        visual.classes,
      )}
    >
      <Icon className={cn(size === 'sm' ? 'h-3 w-3' : 'h-3.5 w-3.5', visual.pulse && 'animate-pulse')} />
      <span className="leading-none whitespace-nowrap">{visual.label}</span>
    </span>
  );
}
